import React, { useMemo } from 'react';
import _get from 'lodash/get';
import _keyBy from 'lodash/keyBy';
import isFieldValidConfigOption from 'helpers/isFieldValidConfigOption';
import * as FormValues from 'types/FormValue';
import { Field } from '@sift/types';
import * as Types from 'pages/Features/typings';

// Components
import LayoutSettingCard from 'molecules/LayoutSettingCard';

interface Props {
  onChange: (field: any, value: FormValues.FormValue) => void;
  userStatus: number;
  fields: Field[];
  values: Types.FeatureValues;
}

export const HIGHLIGHT_SUBTITLE_COPY =
  'Configure which attributes may be highlighted by in your org chart. If no attributes are defined, the feature will be hidden.';

export const HIGHLIGHTABLE_FIELD_TYPES = ['text', 'textSelect', 'boolean', 'number'];

export const highlightByFilter = (field: Field, fields: Field[]) => {
  const fieldTypeIsHighlightable = (field: Field) => HIGHLIGHTABLE_FIELD_TYPES.includes(field.type);

  return (
    fieldTypeIsHighlightable(field) &&
    isFieldValidConfigOption(field) &&
    !parentFieldIsObjectArray(field, fields)
  );
};

// Checks whether a field's direct parent field is an array of objects
function parentFieldIsObjectArray(field: Field, fields: Field[]) {
  if (!field.objectKey.includes('.')) return false;

  const parentFieldKey = field.objectKey.substring(0, field.objectKey.lastIndexOf('.'));
  const parentField = fields.find((field: Field) => field.objectKey === parentFieldKey);

  if (!parentField) return false;

  return fieldIsObjectArray(parentField, fields);
}

// Checks whether a field is an array of objects (education, skills, pageCollection, etc)
function fieldIsObjectArray(field: Field, fields: Field[]) {
  const OBJECT_COLLECTION_ITEM_BASE_FIELD_KEYS = ['object', 'objectCollectionItem'];

  const itemType = _get(field, 'item.type', '');

  if (!_get(field, 'item') || !itemType.startsWith('@fields')) return false;

  if (!fields) return true;

  const childFieldKey = itemType.slice(itemType.indexOf('.') + 1);
  const childField = fields.find((field: Field) => field.objectKey === childFieldKey);

  return (
    !!childField && OBJECT_COLLECTION_ITEM_BASE_FIELD_KEYS.includes(_get(childField, 'baseField'))
  );
}

function OrgChartHighlightsLayout({ userStatus, fields, onChange, values }: Props) {
  const keyedFields = useMemo(() => _keyBy(fields, 'objectKey'), [fields]);

  return (
    <LayoutSettingCard
      cardTitle="Highlighting"
      dialogTitle="Configure Highlighting"
      filter={field => highlightByFilter(field, fields)}
      layoutKey="org-chart-highlight-by"
      subtitle={HIGHLIGHT_SUBTITLE_COPY}
      userStatus={userStatus}
      fields={keyedFields}
      onChange={onChange}
      values={values}
      typeKey="user"
    />
  );
}

export default OrgChartHighlightsLayout;
